/**
 * Role-Based Access Control (RBAC) & Dynamic Permissions Utility
 * Supports Granular Module Actions: view, create, edit, status_change, delete, export, reply
 */

export const DEFAULT_STAFF_PERMISSIONS = {
  dashboard: {
    view: true,
  },
  tenants: {
    view: true,
    create: true,
    edit: true,
    status_change: false,
    delete: false,
  },
  plans: {
    view: true,
    create: false,
    edit: false,
    delete: false,
  },
  transactions: {
    view: true,
    export: true,
  },
  contact_us: {
    view: true,
    reply: true,
    delete: false,
  },
  email_templates: {
    view: true,
    create: false,
    edit: true,
    delete: false,
  },
};

/**
 * Metadata definition for all modules & their allowed granular actions
 */
export const MODULE_PERMISSIONS_SCHEMA = [
  {
    key: "dashboard",
    label: "Dashboard & Analytics",
    description: "Access high-level tenant metrics, KPI counters, and live volume charts.",
    actions: [{ key: "view", label: "View Dashboard" }],
  },
  {
    key: "tenants",
    label: "Tenant Organizations",
    description: "Multi-tenant portal resolution, company dossiers, and logo branding.",
    actions: [
      { key: "view", label: "View Tenants" },
      { key: "create", label: "Create Tenant" },
      { key: "edit", label: "Edit Tenant" },
      { key: "status_change", label: "Activate / Suspend" },
      { key: "delete", label: "Delete Tenant" },
    ],
  },
  {
    key: "plans",
    label: "Subscription Plans",
    description: "Subscription pricing tiers, user limits, and QuickBooks sync connectors.",
    actions: [
      { key: "view", label: "View Plans" },
      { key: "create", label: "Create Plan" },
      { key: "edit", label: "Edit Plan" },
      { key: "delete", label: "Delete Plan" },
    ],
  },
  {
    key: "transactions",
    label: "Super Admin Transactions",
    description: "Global cross-tenant payment audit records, status history, and reporting.",
    actions: [
      { key: "view", label: "View Transactions" },
      { key: "export", label: "Export Records" },
    ],
  },
  {
    key: "contact_us",
    label: "Inquiries & Contact Us",
    description: "Direct customer and prospective client communications, triage, and email replies.",
    actions: [
      { key: "view", label: "View Inquiries" },
      { key: "reply", label: "Reply / Status" },
      { key: "delete", label: "Delete Inquiry" },
    ],
  },
  {
    key: "email_templates",
    label: "Email Templates",
    description: "Transactional notifications, tenant activation emails, and invoice templates.",
    actions: [
      { key: "view", label: "View Templates" },
      { key: "create", label: "Create Template" },
      { key: "edit", label: "Edit Template" },
      { key: "delete", label: "Delete Template" },
    ],
  },
];

/**
 * Extracts active permissions object from Redux or sessionStorage
 */
export const getActiveUserPermissions = (userState) => {
  if (!userState) {
    try {
      const stored = sessionStorage.getItem("logedInUser");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.permissions) return parsed.permissions;
        if (parsed?.subRole?.permissions?.[0]) return parsed.subRole.permissions[0];
      }
    } catch (e) {}
    return null;
  }

  if (userState.permissions && typeof userState.permissions === "object") {
    return userState.permissions;
  }
  if (userState.subRole?.permissions?.[0]) {
    return userState.subRole.permissions[0];
  }
  return null;
};

/**
 * Checks whether user is root Super Admin
 */
export const isUserSuperAdmin = (userState) => {
  if (!userState) return true; // default safe
  if (userState.isSuperAdmin) return true;
  if (userState.role === "super-admin" || userState.role?.name === "Super Admin") return true;
  // If neither subRole nor permissions exist, root super admin account
  if (!userState.subRole && !userState.permissions) return true;
  return false;
};

/**
 * Primary Dynamic RBAC Check: hasPermission(user, module, action)
 * e.g. hasPermission(user, "tenants", "create")
 */
export const hasPermission = (userState, moduleName, action = "view") => {
  if (!userState) return true;
  if (isUserSuperAdmin(userState)) return true;

  const perms = getActiveUserPermissions(userState);
  if (!perms) return true;

  // Normalize module key: contactUs -> contact_us, etc.
  const normalizedKey = String(moduleName || "")
    .replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
    .replace(/-/g, "_")
    .replace(/^_/, "");

  const modulePerm = perms[normalizedKey] || perms[moduleName];

  if (modulePerm && typeof modulePerm === "object") {
    if (action in modulePerm) {
      return Boolean(modulePerm[action]);
    }
    return Boolean(modulePerm.view);
  }

  // Fallback to legacy flat keys
  const legacyActionKey = `${moduleName}_${action}`;
  if (legacyActionKey in perms) return Boolean(perms[legacyActionKey]);
  if (moduleName in perms) return Boolean(perms[moduleName]);

  return false;
};

/**
 * Calculates total granted permissions count
 */
export const countPermissions = (permissions = {}) => {
  let granted = 0;
  let total = 0;

  MODULE_PERMISSIONS_SCHEMA.forEach((mod) => {
    mod.actions.forEach((act) => {
      total += 1;
      if (permissions?.[mod.key]?.[act.key]) {
        granted += 1;
      }
    });
  });

  return { granted, total };
};
